<template>
  <v-container class="h-full">
    <div class="flex flex-row gap-2 text-sm">
      <router-link :to="{ name: RouteNames.PROGRAM_LIST }">
        Programs
      </router-link>
      /
      <router-link
        v-if="program"
        :to="{ name: RouteNames.PROGRAM_EDIT, params: { id: program.id } }"
      >
        {{ program.name }}
      </router-link>
      <span v-else>{{ programId }}</span>
      /
      <span>{{ versionLabel }}</span>
    </div>

    <div class="d-flex align-start items-center justify-space-between ga-4 mt-3">
      <h1>
        <template v-if="loading">
          Loading Version...
        </template>
        <template v-else-if="!version">
          Version Not Found
        </template>
        <template v-else>
          {{ releaseName }}
        </template>
      </h1>
      <v-btn
        v-if="showPinButton"
        key="pin"
        color="primary"
        :loading="pinning"
        :disabled="!currentEnvironment || pinning"
        @click="pinVersion"
      >
        Pin Version to {{ currentEnvironment?.name }}
      </v-btn>
      <v-btn
        v-else-if="showUnpinButton"
        key="unpin"
        color="warning"
        variant="tonal"
        :loading="unpinning"
        :disabled="unpinning"
        @click="unpinVersion"
      >
        Unpin Version from {{ currentEnvironment?.name }}
      </v-btn>
    </div>
    <div class="flex gap-1 opacity-50 mb-4">
      <p><strong>Tag:</strong></p>
      <p class="break-all" v-text="versionTag || 'Loading...'" />
      <p class="ml-4">
        <strong>Commit:</strong>
      </p>
      <p class="break-all" v-text="version?.commitSha || 'Loading...'" />
    </div>

    <div>
      <v-alert
        v-if="errorMessage"
        type="error"
        variant="tonal"
        class="mb-4"
        :text="errorMessage"
        closable
        @click:close="errorMessage = ''"
      />

      <v-alert
        v-if="currentPin"
        key="pinned"
        :type="pinnedVersionAlertType"
        variant="tonal"
        class="mb-4"
        :text="pinnedVersionMessage"
      />

      <v-card color="darknavyblue" elevation="5" class="pa-4 mb-4">
        <v-card-title>Changelog</v-card-title>
        <v-card-text>
          <v-progress-circular v-if="loading || diffLoading" indeterminate />
          <p v-else-if="!version" class="opacity-50">
            Version not found.
          </p>
          <v-alert
            v-else-if="!previousVersion"
            key="diff-initial"
            type="info"
            variant="tonal"
            text="This is the initial version, so there is no previous tag to compare."
          />
          <v-alert
            v-else-if="diffErrorMessage"
            key="diff-error"
            type="error"
            variant="tonal"
            :text="diffErrorMessage"
          />
          <template v-else>
            <div class="mb-3 text-sm opacity-70">
              <span>Comparing </span>
              <span class="font-mono" v-text="previousVersion.tag" />
              <span> to </span>
              <span class="font-mono" v-text="version.tag" />
            </div>
            <div class="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] gap-4">
              <div class="min-w-0">
                <p class="uppercase opacity-50 mb-2">
                  Changed Files
                </p>
                <v-list
                  v-if="changedPaths.length"
                  bg-color="transparent"
                  class="pa-0 max-h-[640px] overflow-auto"
                >
                  <v-list-item
                    v-for="path of changedPaths"
                    :key="path"
                    variant="tonal"
                    border
                    class="mb-1 !px-3"
                  >
                    <p class="font-mono text-sm break-all" v-text="path" />
                  </v-list-item>
                </v-list>
                <p v-else class="opacity-50">
                  No changed files returned.
                </p>
              </div>
              <div class="min-w-0">
                <p class="uppercase opacity-50 mb-2">
                  Unified Diff
                </p>
                <pre
                  v-if="unifiedDiff"
                  class="overflow-auto pa-4 rounded bg-black text-white text-sm max-h-[640px]"
                  v-text="unifiedDiff"
                />
                <p v-else class="opacity-50">
                  No diff returned for this version.
                </p>
              </div>
            </div>
          </template>
        </v-card-text>
      </v-card>

      <v-card color="darknavyblue" elevation="5" class="pa-4 mb-4">
        <v-card-title>File Tree</v-card-title>
        <v-card-text>
          <v-progress-circular v-if="loading || fileTreeLoading" indeterminate />
          <p v-else-if="!version" class="opacity-50">
            Version not found.
          </p>
          <v-alert
            v-else-if="fileTreeErrorMessage"
            type="error"
            variant="tonal"
            :text="fileTreeErrorMessage"
          />
          <template v-else>
            <v-alert
              v-if="fileDownloadErrorMessage"
              type="error"
              variant="tonal"
              class="mb-3"
              :text="fileDownloadErrorMessage"
              closable
              @click:close="fileDownloadErrorMessage = ''"
            />
            <v-list
              v-if="fileTreeEntries.length"
              bg-color="transparent"
              class="pa-0 max-h-[640px] overflow-auto"
            >
              <v-list-item
                v-for="entry of fileTreeEntries"
                :key="entry.path"
                variant="tonal"
                border
                class="mb-1 !px-3"
              >
                <div class="d-flex align-center ga-3 min-w-0">
                  <v-icon icon="mdi-file-document-outline" size="18" class="flex-shrink-0 opacity-70" />
                  <p class="font-mono text-sm break-all flex-1" v-text="entry.path" />
                  <p class="font-mono text-xs opacity-60 flex-shrink-0" v-text="formatFileSize(entry.size)" />
                  <v-btn
                    icon="mdi-download"
                    size="32"
                    variant="text"
                    :loading="downloadingFilePath === entry.path"
                    :disabled="!!downloadingFilePath"
                    @click="downloadFile(entry)"
                  />
                </div>
              </v-list-item>
            </v-list>
            <p v-else class="opacity-50">
              No files returned for this version.
            </p>
          </template>
        </v-card-text>
      </v-card>
    </div>
  </v-container>
</template>

<script lang="ts">
import { storeToRefs } from 'pinia'
import { computed, defineComponent, onBeforeUnmount, ref, shallowRef, watch } from 'vue'
import { useRoute } from 'vue-router'

import { useApiStore } from '@/stores/api'
import { useDomainStore } from '@/stores/domain'
import { useEnvironmentStore } from '@/stores/environment'
import { useReleaseStore } from '@/stores/release'

import { RouteNames } from '@/router'
import { bytesToObjectURL } from '@/utils/api'
import { canPinReleaseVersion } from '@/utils/program'

import type { Program, ReadUserMembershipsResponse_ProgramContext } from '@policyfly/protobuf/pfbingo'
import type { ReleasePin, TreeEntry, Version } from '@policyfly/protobuf/pfrelease'

const PIN_REFRESH_INTERVAL = 10_000

export default defineComponent({
  name: 'ProgramVersion',

  setup () {
    const route = useRoute()
    const apiStore = useApiStore()
    const release = useReleaseStore()
    const { pins: releasePins } = storeToRefs(release)
    const { currentDomain } = storeToRefs(useDomainStore())
    const { currentEnvironment } = storeToRefs(useEnvironmentStore())

    const loading = ref(true)
    const diffLoading = ref(false)
    const fileTreeLoading = ref(false)
    const pinning = ref(false)
    const unpinning = ref(false)
    const errorMessage = ref('')
    const diffErrorMessage = ref('')
    const fileTreeErrorMessage = ref('')
    const fileDownloadErrorMessage = ref('')
    const downloadingFilePath = ref('')
    const program = shallowRef<Program | null>(null)
    const version = shallowRef<Version | null>(null)
    const previousVersion = shallowRef<Version | null>(null)
    const changedPaths = ref<string[]>([])
    const fileTreeEntries = ref<TreeEntry[]>([])
    const userMemberships = shallowRef<ReadUserMembershipsResponse_ProgramContext[]>([])
    const unifiedDiff = ref('')

    const programId = computed(() => normalizeRouteParam(route.params.id))
    const versionTag = computed(() => normalizeRouteParam(route.params.tag))
    const versionLabel = computed(() => {
      if (version.value?.version) return `v${version.value.version}`
      return versionTag.value || ''
    })
    const releaseName = computed(() => {
      if (!version.value) return ''
      const programName = program.value?.name || program.value?.slug || 'Program'
      return `${programName} Version ${versionLabel.value}`
    })
    const loadedPins = computed<ReleasePin[]>(() => {
      const slug = program.value?.slug
      if (!slug) return []
      return releasePins.value.filter((pin) => pin.slug === slug)
    })
    const pendingPins = computed(() => {
      return loadedPins.value.filter((pin) => !['failed', 'synced'].includes(pin.status))
    })
    const currentPin = computed(() => {
      const tag = version.value?.tag
      const environmentId = currentEnvironment.value?.id
      if (!tag || !environmentId) return undefined
      return loadedPins.value.find((pin) => pin.tag === tag && pin.env === environmentId)
    })
    const currentProgramMembership = computed(() => {
      const currentProgramId = program.value?.id
      if (!currentProgramId) return null
      return userMemberships.value.find((membership) => {
        return membership.programId === currentProgramId || membership.program?.id === currentProgramId
      }) ?? null
    })
    const canPinVersion = computed(() => {
      return canPinReleaseVersion(currentProgramMembership.value?.role, currentEnvironment.value?.name)
    })
    const showPinButton = computed(() => {
      return canPinVersion.value && !!version.value && !currentPin.value
    })
    const showUnpinButton = computed(() => {
      return canPinVersion.value && !!version.value && !!currentPin.value
    })
    const pinnedVersionAlertType = computed(() => {
      return currentPin.value?.status === 'synced' ? 'success' : 'info'
    })
    const pinnedVersionMessage = computed(() => {
      const envName = currentEnvironment.value?.name ?? 'the current'
      const status = currentPin.value?.status
      const statusText = status ? ` Status: ${status}.` : ''
      return `This version is pinned to the ${envName} environment.${statusText}`
    })

    watch(() => ({ id: programId.value, tag: versionTag.value }), async ({ id, tag }) => {
      loading.value = true
      diffLoading.value = false
      fileTreeLoading.value = false
      errorMessage.value = ''
      diffErrorMessage.value = ''
      fileTreeErrorMessage.value = ''
      fileDownloadErrorMessage.value = ''
      downloadingFilePath.value = ''
      program.value = null
      version.value = null
      previousVersion.value = null
      changedPaths.value = []
      fileTreeEntries.value = []
      unifiedDiff.value = ''

      try {
        if (!id) throw new Error('Program id is required.')
        if (!tag) throw new Error('Version tag is required.')

        const { program: loadedProgram } = await apiStore.loadProgramDetails(id)
        if (!loadedProgram) throw new Error('Program not found.')
        program.value = loadedProgram

        const { versions } = await apiStore.listVersionsForProgram({ slug: loadedProgram.slug })
        const versionIndex = versions.findIndex((releaseVersion) => releaseVersion.tag === tag)
        const loadedVersion = versions[versionIndex]
        if (!loadedVersion) throw new Error('Version not found.')
        version.value = loadedVersion

        const previous = versions[versionIndex + 1] ?? null
        previousVersion.value = previous
        loading.value = false

        await Promise.all([
          loadFileTree(loadedProgram.slug, loadedVersion.tag),
          loadDiff(loadedProgram.slug, previous, loadedVersion),
        ])
      } catch (err) {
        console.error(err)
        errorMessage.value = err instanceof Error ? err.message : 'Failed to load program version.'
      } finally {
        loading.value = false
      }
    }, { immediate: true })

    async function loadFileTree (slug: string, tag: string): Promise<void> {
      fileTreeLoading.value = true
      try {
        const { entries } = await apiStore.getFileTree({
          slug,
          ref: tag,
        })
        fileTreeEntries.value = entries.slice().sort((a, b) => a.path.localeCompare(b.path))
      } catch (err) {
        console.error(err)
        fileTreeErrorMessage.value = err instanceof Error ? err.message : 'Failed to load file tree.'
      } finally {
        fileTreeLoading.value = false
      }
    }

    async function loadDiff (slug: string, previous: Version | null, current: Version): Promise<void> {
      if (!previous) return
      diffLoading.value = true
      try {
        const diff = await apiStore.getDiff({
          slug,
          fromTag: previous.tag,
          toTag: current.tag,
        })
        changedPaths.value = diff.changedPaths.slice().sort((a, b) => a.localeCompare(b))
        unifiedDiff.value = diff.unifiedDiff
      } catch (err) {
        console.error(err)
        diffErrorMessage.value = err instanceof Error ? err.message : 'Failed to load version diff.'
      } finally {
        diffLoading.value = false
      }
    }

    watch(() => ({
      domain: currentDomain.value,
      environmentId: currentEnvironment.value?.id,
      userId: apiStore.userId,
    }), async ({ environmentId, userId }) => {
      if (!environmentId || !userId) {
        userMemberships.value = []
        return
      }
      await loadCurrentUserMemberships(userId, environmentId)
    }, { immediate: true })

    const refreshInterval = ref<number | null>(null)
    function stopRefreshInterval (): void {
      if (!refreshInterval.value) return
      window.clearInterval(refreshInterval.value)
      refreshInterval.value = null
    }
    function startRefreshInterval (): void {
      if (refreshInterval.value) return
      refreshInterval.value = window.setInterval(() => {
        if (!pendingPins.value.length) {
          stopRefreshInterval()
          return
        }
        release.loadMatrix()
      }, PIN_REFRESH_INTERVAL)
    }
    watch(pendingPins, (pending) => {
      if (pending.length) startRefreshInterval()
      else stopRefreshInterval()
    }, { immediate: true })
    onBeforeUnmount(stopRefreshInterval)

    async function loadCurrentUserMemberships (userId: string, environmentId: string): Promise<void> {
      try {
        const { memberships } = await apiStore.loadUserMemberships({
          userId,
          environmentId,
          domain: currentDomain.value,
        })
        userMemberships.value = Object.values(memberships)
      } catch (err) {
        console.error(err)
        userMemberships.value = []
      }
    }

    async function pinVersion (): Promise<void> {
      if (!canPinVersion.value || !program.value || !version.value || !currentEnvironment.value || pinning.value) return
      if (!confirm(`Are you sure you want to pin ${program.value.slug.toLocaleUpperCase()} ${versionLabel.value} to the ${currentEnvironment.value.name} environment?`)) return
      pinning.value = true
      errorMessage.value = ''
      try {
        await release.pinVersion(program.value.slug, version.value.tag)
      } catch (err) {
        console.error(err)
        errorMessage.value = err instanceof Error ? err.message : 'Failed to pin version.'
      } finally {
        pinning.value = false
      }
    }

    async function unpinVersion (): Promise<void> {
      if (!canPinVersion.value || !program.value || !version.value || !currentEnvironment.value || unpinning.value) return
      const envName = currentEnvironment.value.name
      const message = `Are you sure you want to unpin ${program.value.slug.toLocaleUpperCase()} ${versionLabel.value} from the ${envName} environment? ` +
        `It will return to the global tag on the next ${envName} deploy.`
      if (!confirm(message)) return
      unpinning.value = true
      errorMessage.value = ''
      try {
        await release.unpinVersion(program.value.slug)
      } catch (err) {
        console.error(err)
        errorMessage.value = err instanceof Error ? err.message : 'Failed to unpin version.'
      } finally {
        unpinning.value = false
      }
    }

    function normalizeRouteParam (param: unknown): string {
      if (Array.isArray(param)) return param.join('/')
      if (typeof param === 'string') return param
      return ''
    }

    async function downloadFile (entry: TreeEntry): Promise<void> {
      if (!program.value || !version.value || downloadingFilePath.value) return

      downloadingFilePath.value = entry.path
      fileDownloadErrorMessage.value = ''
      try {
        const { content } = await apiStore.getFile({
          slug: program.value.slug,
          ref: version.value.tag,
          path: entry.path,
        })
        const url = bytesToObjectURL(content, 'application/octet-stream')
        const link = document.createElement('a')
        link.href = url
        link.download = downloadFileName(entry.path)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.setTimeout(() => URL.revokeObjectURL(url), 0)
      } catch (err) {
        console.error(err)
        fileDownloadErrorMessage.value = err instanceof Error ? err.message : `Failed to download ${entry.path}.`
      } finally {
        downloadingFilePath.value = ''
      }
    }

    function downloadFileName (path: string): string {
      return path.split('/').pop() || 'download'
    }

    function formatFileSize (size: string): string {
      const bytes = Number(size)
      if (!Number.isFinite(bytes)) return size
      return `${new Intl.NumberFormat('en-US', {
        notation: bytes >= 1_000_000 ? 'compact' : 'standard',
        maximumFractionDigits: 1,
      }).format(bytes)} B`
    }

    return {
      RouteNames,
      loading,
      diffLoading,
      fileTreeLoading,
      pinning,
      unpinning,
      errorMessage,
      diffErrorMessage,
      fileTreeErrorMessage,
      fileDownloadErrorMessage,
      downloadingFilePath,
      program,
      programId,
      version,
      previousVersion,
      versionTag,
      versionLabel,
      releaseName,
      currentEnvironment,
      currentPin,
      showPinButton,
      showUnpinButton,
      pinnedVersionAlertType,
      pinnedVersionMessage,
      changedPaths,
      fileTreeEntries,
      unifiedDiff,
      pinVersion,
      unpinVersion,
      downloadFile,
      formatFileSize,
    }
  },
})
</script>
